<template>
  <div>
    <v-bottom-sheet
      :content-class="['app-upload-tray', {'app-upload-tray--minimized': minimized}]"
      :model-value="!!store.items.length"
      :scrim="false"
      attach
      max-width="600"
      no-click-animation
      persistent
      scroll-strategy="none"
    >
      <v-card class="app-upload-tray__card">
        <v-card-title class="app-upload-tray__card__header d-flex align-center">
          {{ store.hasActive ? 'Upload In Progress' : 'Uploads Finished' }}
          <v-spacer />
          <v-btn
            :aria-label="minimized ? 'Expand' : 'Minimize'"
            :icon="minimized ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            color="secondary"
            density="comfortable"
            size="small"
            variant="text"
            @click="minimized = !minimized"
          />
        </v-card-title>
        <v-card-text class="app-upload-tray__card__body">
          <v-list>
            <v-list-item
              v-for="item in store.items"
              :key="item.id"
              lines="three"
            >
              <v-list-item-title>{{ item.name }}</v-list-item-title>
              <v-list-item-subtitle>{{ $file.formatFileSize(item.size) }}</v-list-item-subtitle>
              <div class="d-flex align-center ga-3 mt-2">
                <v-progress-linear
                  :color="progressColor(item)"
                  :indeterminate="item.indeterminate"
                  :model-value="item.progress"
                  bg-color="newspaper"
                  bg-opacity="0.6"
                  height="6"
                  rounded
                />
                <span class="app-upload-tray__percent">{{ item.progress }}%</span>
                <div class="app-upload-tray__status">
                  <v-icon
                    v-if="item.status === 'success'"
                    color="primary"
                    icon="mdi-check-circle"
                    size="20"
                  />
                  <v-btn
                    v-else-if="item.status === 'error'"
                    aria-label="Dismiss"
                    color="secondary"
                    density="comfortable"
                    icon="mdi-close"
                    size="x-small"
                    variant="text"
                    @click="store.remove(item.id)"
                  />
                </div>
              </div>
              <v-messages
                :active="!!item.message"
                :color="item.status === 'error' ? 'error' : undefined"
                :messages="[item.message!]"
                class="mt-1 mb-1"
              />
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script lang="ts" setup>
import {onBeforeUnmount, onMounted, ref} from "vue"
import {type UploadItem, useUploadStore} from "@/stores/upload"

const store = useUploadStore()
const minimized = ref(false)

const progressColor = (item: UploadItem): string => {
  return item.status === "error" ? "error" : "primary"
}

const warnBeforeUnload = (event: BeforeUnloadEvent) => {
  if (!store.hasActive) {
    return
  }

  event.preventDefault()

  // Legacy browsers only show the prompt when returnValue is set
  event.returnValue = ""
}

onMounted(() => {
  window.addEventListener("beforeunload", warnBeforeUnload)
})

onBeforeUnmount(() => {
  window.removeEventListener("beforeunload", warnBeforeUnload)
})
</script>

<style lang="scss" scoped>
@use "@/scss/settings";
@use "sass:map";

:deep(.app-upload-tray) {
  position: fixed !important;
  bottom: 0;
  left: unset !important;

  box-shadow: unset !important;
  transition: transform 0.3s ease-in-out !important;
  transform: translateY(0) !important;

  @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
    right: 16px !important;
  }
}

:deep(.app-upload-tray--minimized) {
  transform: translateY(calc(100% - 45px)) !important;
}

.app-upload-tray__card {
  border-top-left-radius: 16px !important;
  border-top-right-radius: 16px !important;

  &__header {
    padding: 6px 12px 6px 24px;
    font-size: 1rem;
    font-weight: 500;
    border-bottom: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  }

  &__body {
    max-height: 400px;
    overflow-y: auto;
  }
}

.app-upload-tray__status {
  display: flex;
  flex: 0 0 24px;
  justify-content: center;
}

.app-upload-tray__percent {
  min-width: 40px;
  text-align: right;
  font-size: 0.875rem;
  font-variant-numeric: tabular-nums;
}
</style>
